/** Roll crmApiUsage rows into a stacked daily chart and a breakdown table. */

export type ApiUsageGroupBy = 'user' | 'crmGroup' | 'module' | 'action' | 'decision';

export type ApiUsageEvent = {
  id: string;
  uid?: string | null;
  displayName?: string | null;
  crmGroup?: string | null;
  crmRole?: string | null;
  action?: string | null;
  endpoint?: string | null;
  agentId?: string | null;
  decision?: string | null;
  reason?: string | null;
  keyId?: string | null;
  ts?: Date | null;
};

export type ApiUsageSeries = {
  /** Recharts data key on each point. */
  key: string;
  /** Stable group id (uid, module, …). `__other__` is the chart overflow bucket. */
  id: string;
  label: string;
};

export type ApiUsageTotal = {
  id: string;
  label: string;
  calls: number;
  denies: number;
};

export type ApiUsagePoint = {
  day: string;
  label: string;
} & Record<string, number | string>;

export type ApiUsageChart = {
  series: ApiUsageSeries[];
  points: ApiUsagePoint[];
  totals: ApiUsageTotal[];
  drillName: string | null;
  denied: ApiUsageEvent[];
};

const CHART_SERIES_CAP = 8;
const OTHER_ID = '__other__';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function apiUsageDayKey(ts?: Date | null): string | null {
  if (!ts || Number.isNaN(ts.getTime())) return null;
  return `${ts.getUTCFullYear()}-${String(ts.getUTCMonth() + 1).padStart(2, '0')}-${String(ts.getUTCDate()).padStart(2, '0')}`;
}

function dayLabel(day: string): string {
  const [, month, date] = day.split('-');
  const monthIndex = Number(month) - 1;
  const name = MONTHS[monthIndex] || month;
  return `${name} ${Number(date)}`;
}

function eachUtcDay(from: Date, to: Date): string[] {
  const start = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const end = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  const days: string[] = [];
  for (let t = start; t <= end; t += 86_400_000) {
    const key = apiUsageDayKey(new Date(t));
    if (key) days.push(key);
  }
  return days;
}

function titleCaseToken(raw: string): string {
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function moduleIdentity(row: ApiUsageEvent): { id: string; label: string } {
  const agent = row.agentId?.trim();
  const endpoint = row.endpoint?.trim();
  const raw = agent || endpoint || 'unknown';
  const stripped = raw.replace(/Agent$/i, '').replace(/^direct\s+/i, '');
  return { id: raw, label: titleCaseToken(stripped) || raw };
}

function groupIdentity(row: ApiUsageEvent, groupBy: ApiUsageGroupBy): { id: string; label: string } {
  if (groupBy === 'user') {
    const id = row.uid?.trim() || 'unknown';
    const name = row.displayName?.trim();
    return { id, label: name || (id === 'unknown' ? 'Unknown' : id.slice(0, 8)) };
  }
  if (groupBy === 'crmGroup') {
    const id = row.crmGroup?.trim() || row.crmRole?.trim() || 'unknown';
    return { id, label: titleCaseToken(id) || id };
  }
  if (groupBy === 'module') return moduleIdentity(row);
  if (groupBy === 'action') {
    const id = row.action?.trim() || 'unknown';
    return { id, label: id };
  }
  const decision = (row.decision || '').trim().toLowerCase();
  if (decision === 'deny') return { id: 'deny', label: 'Denied' };
  if (decision === 'allow') return { id: 'allow', label: 'Allowed' };
  return { id: decision || 'other', label: titleCaseToken(decision || 'other') };
}

export function buildApiUsageChart(args: {
  rows: ApiUsageEvent[];
  groupBy: ApiUsageGroupBy;
  drillUid?: string | null;
  from: Date;
  to: Date;
}): ApiUsageChart {
  const drillUid = args.drillUid?.trim() || null;
  const scoped = drillUid ? args.rows.filter((row) => (row.uid || 'unknown') === drillUid) : args.rows;
  const days = eachUtcDay(args.from, args.to);
  const daySet = new Set(days);

  type Acc = { id: string; label: string; calls: number; denies: number; byDay: Map<string, number> };
  const groups = new Map<string, Acc>();
  let drillName: string | null = null;

  for (const row of scoped) {
    if (drillUid && !drillName) {
      drillName = row.displayName?.trim() || (drillUid === 'unknown' ? 'Unknown' : drillUid.slice(0, 8));
    }
    const identity = groupIdentity(row, args.groupBy);
    let acc = groups.get(identity.id);
    if (!acc) {
      acc = { id: identity.id, label: identity.label, calls: 0, denies: 0, byDay: new Map() };
      groups.set(identity.id, acc);
    }
    acc.calls += 1;
    if (row.decision === 'deny') acc.denies += 1;
    const day = apiUsageDayKey(row.ts);
    if (day && daySet.has(day)) acc.byDay.set(day, (acc.byDay.get(day) || 0) + 1);
  }

  if (drillUid && !drillName) {
    drillName = drillUid === 'unknown' ? 'Unknown' : drillUid.slice(0, 8);
  }

  const totals: ApiUsageTotal[] = Array.from(groups.values())
    .map(({ id, label, calls, denies }) => ({ id, label, calls, denies }))
    .sort((a, b) => b.calls - a.calls || a.label.localeCompare(b.label));

  const chartGroups = totals.slice(0, CHART_SERIES_CAP);
  const overflow = totals.slice(CHART_SERIES_CAP);
  const series: ApiUsageSeries[] = chartGroups.map((group, index) => ({
    key: `s${index}`,
    id: group.id,
    label: group.label,
  }));
  if (overflow.length) {
    series.push({ key: 'sOther', id: OTHER_ID, label: 'Other' });
  }

  const points: ApiUsagePoint[] = days.map((day) => {
    const point: ApiUsagePoint = { day, label: dayLabel(day) };
    chartGroups.forEach((group, index) => {
      point[`s${index}`] = groups.get(group.id)?.byDay.get(day) || 0;
    });
    if (overflow.length) {
      point.sOther = overflow.reduce((sum, group) => sum + (groups.get(group.id)?.byDay.get(day) || 0), 0);
    }
    return point;
  });

  const denied = scoped
    .filter((row) => row.decision === 'deny')
    .sort((a, b) => (b.ts?.getTime() || 0) - (a.ts?.getTime() || 0));

  return { series, points, totals, drillName, denied };
}
